/**
 * Component: Call for price
 * Parks the Swym PDP heart next to the Call action and keeps the financing promo off a page
 * with no buy path. Opening the chat from the Text action is not here: that anchor carries
 * data-birdeye-chat-open and goes through the delegation in birdeye-chat.js, which keeps the
 * sms: fallback when no widget is available.
 */
(() => {
  const WISHLIST_SLOT_SELECTOR = "[data-call-for-price-wishlist]";
  const WISHLIST_BUTTON_SELECTOR = "#swym-custom-pdp-button, .swym-custom-pdp-button";
  const PRODUCT_INFO_SELECTOR = ".wt-product__info";
  const APP_BLOCK_SELECTOR = ".shopify-block";
  const HIDDEN_STATE_CLASS = "_hidden";
  const OBSERVED_MUTATIONS = { childList: true, subtree: true };

  class CallForPrice extends HTMLElement {
    constructor() {
      super();
      this.scheduleSync = this.scheduleSync.bind(this);
      this.syncFrame = null;
      this.infoObserver = null;
    }

    connectedCallback() {
      this.productInfo = this.closest(PRODUCT_INFO_SELECTOR);
      this.designMode = window.Shopify?.designMode === true;
      this.sync();

      if (this.productInfo) {
        this.infoObserver = new MutationObserver(this.scheduleSync);
        this.infoObserver.observe(this.productInfo, OBSERVED_MUTATIONS);
      }
    }

    disconnectedCallback() {
      this.infoObserver?.disconnect();
      this.infoObserver = null;

      if (this.syncFrame) cancelAnimationFrame(this.syncFrame);
      this.syncFrame = null;
    }

    scheduleSync() {
      if (this.syncFrame) return;

      this.syncFrame = requestAnimationFrame(() => {
        this.syncFrame = null;
        this.sync();
      });
    }

    sync() {
      this.infoObserver?.disconnect();

      try {
        this.adoptWishlist();
        this.hideFinancingMessaging();
      } finally {
        if (this.infoObserver && this.productInfo) {
          this.infoObserver.takeRecords();
          this.infoObserver.observe(this.productInfo, OBSERVED_MUTATIONS);
        }
      }
    }

    hideFinancingMessaging() {
      if (!this.productInfo || this.designMode) return;

      const appName = this.dataset.financingApp;
      if (!appName) return;

      const needle = appName.replace(/['\\]/g, "\\$&");
      const selector = `iframe[src*='${needle}' i], [class*='${needle}' i]`;
      this.productInfo.querySelectorAll(selector).forEach((node) => {
        const target = node.closest(APP_BLOCK_SELECTOR) || node;
        target.classList.add(HIDDEN_STATE_CLASS);
      });
    }

    /** Swym re-inserts the heart next to the title; keep parking it next to Call. */
    adoptWishlist() {
      const slot = this.querySelector(WISHLIST_SLOT_SELECTOR);
      if (!slot) return;

      const root = this.productInfo || this;
      const buttons = [...root.querySelectorAll(WISHLIST_BUTTON_SELECTOR)];
      if (!buttons.length) return;

      const keeper = buttons.find((node) => slot.contains(node)) || buttons[0];
      if (!slot.contains(keeper)) slot.appendChild(keeper);
      keeper.classList.remove(HIDDEN_STATE_CLASS);

      buttons.forEach((node) => {
        if (node === keeper) return;
        node.classList.add(HIDDEN_STATE_CLASS);
      });
    }
  }

  if (!customElements.get("call-for-price")) {
    customElements.define("call-for-price", CallForPrice);
  }
})();
